import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { AdvShell } from "@/components/AdvShell";
import { StatusBadge, Metric, money, num, STATUS_HELP } from "./advUtils";
import { toast } from "sonner";
import { ChevronLeft } from "lucide-react";

const src = (u) => (u ? (u.startsWith("http") ? u : `${process.env.REACT_APP_BACKEND_URL}${u}`) : null);

export default function AdvCampaignDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [c, setC] = useState(null);
  const load = () => api.get(`/adv/campaigns/${id}`).then((r) => setC(r.data)).catch(() => nav("/advertiser/campaigns"));
  useEffect(() => { load(); }, [id]);
  if (!c) return <AdvShell><div className="text-race-sub">Loading…</div></AdvShell>;

  const act = async (fn, ok) => { try { await fn(); toast.success(ok); load(); } catch (e) { toast.error(apiError(e.response?.data?.detail)); } };

  return (
    <AdvShell>
      <button onClick={() => nav("/advertiser/campaigns")} className="text-sm text-race-sub hover:text-race-txt mb-3 inline-flex items-center gap-1"><ChevronLeft size={15} /> Campaigns</button>
      <div className="flex items-center gap-3 mb-1 flex-wrap"><h1 className="font-heading text-4xl tracking-tight" data-testid="adv-detail-title">{c.name}</h1><StatusBadge status={c.status} label={c.status_label} /></div>
      <p className="text-race-sub mb-5">{STATUS_HELP[c.status]}</p>

      {c.review_message && (
        <div className="mb-5 text-sm rounded-md border border-orange-500/30 bg-orange-500/10 text-orange-200 px-4 py-3" data-testid="adv-detail-review">
          <b>Message from the review team:</b> {c.review_message}
          {c.status === "changes_requested" && <Link to={`/advertiser/campaigns/${id}/edit`} className="block mt-2 text-race-volt hover:underline">Edit & resubmit →</Link>}
        </div>
      )}

      <div className="grid lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2 space-y-5">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <Metric label="Spend" value={money(c.spend_usd)} sub={`Billed ${money(c.billed_usd)}`} />
            <Metric label="Unbilled" value={money(c.unbilled_usd)} />
            <Metric label="Impressions" value={num(c.impressions)} />
            <Metric label="CTR" value={`${c.ctr}%`} />
          </div>
          <div className="bg-race-surface border border-race-line rounded-lg p-5 space-y-2 text-sm">
            <Row k="Budget" v={money(c.budget_usd)} />
            <Row k="Remaining" v={money(c.remaining_usd)} />
            <Row k="CPM" v={money(c.cpm_usd)} />
            <Row k="Dates" v={c.start_date ? `${c.start_date.slice(0, 10)} → ${(c.end_date || "").slice(0, 10)}` : "—"} />
            <Row k="Destination" v={c.click_url || "—"} />
            <Row k="Audience" v={c.interests?.length ? c.interests.join(", ") : "General Audience"} />
          </div>
        </div>
        <div className="space-y-4">
          <div className="bg-race-surface border border-race-line rounded-lg overflow-hidden">
            <div className="text-xs uppercase text-race-muted px-4 pt-3">Creative</div>
            <div className="p-4">{c.image_url && <img src={src(c.image_url)} alt="" className="w-full rounded border border-race-line" data-testid="adv-detail-creative" />}</div>
          </div>
          {c.status === "active" && <Link to={`/advertiser/analytics?c=${id}`} className="block text-center bg-race-txt text-race-bg font-semibold text-sm py-2.5 rounded-md">View Analytics</Link>}
          {c.status === "active" && <button onClick={() => act(() => api.post(`/adv/campaigns/${id}/pause`), "Paused")} className="w-full border border-race-line rounded-md py-2 text-sm hover:border-race-volt">Pause campaign</button>}
          {c.status === "paused" && <button onClick={() => act(() => api.post(`/adv/campaigns/${id}/resume`), "Resumed")} className="w-full bg-race-volt text-race-bg font-semibold rounded-md py-2.5 text-sm">Resume campaign</button>}
          {c.status === "completed" && <button onClick={() => act(async () => { const { data } = await api.post(`/adv/campaigns/${id}/duplicate`); nav(`/advertiser/campaigns/${data.id}/edit`); }, "Duplicated")} className="w-full border border-race-line rounded-md py-2 text-sm hover:border-race-volt">Duplicate</button>}
        </div>
      </div>
    </AdvShell>
  );
}
const Row = ({ k, v }) => (<div className="flex justify-between gap-4 py-1.5 border-b border-race-line/60 last:border-0"><span className="text-race-sub">{k}</span><span className="text-race-txt text-right">{v}</span></div>);
